import moment from 'moment'
import { arrayOf, bool, func, number, object, string } from 'prop-types'
import { Component } from 'react'
import { Timescale } from '@opallabs/concepts'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { AnalyticsLayer } from '@opallabs/quanta'
import { ContentFilter } from '../ContentFilter'
import Form from '../Form'
import HotKey from '../HotKey'
import { TextInput } from '../Inputs'
import Modal from '../Modal/unmanaged'
import { kuiperClassNames } from '../utils'

import './styles.scss'

const HOTKEY = 'shift+alt+e'
const MAX_ROW_COUNT = 10000
const TOO_MANY_ROWS_MESSAGE =
  '10,000 row limit exceeded. Please narrow your selection.'

const defaultEndDate = (props) => {
  const { duration, startDate } = props
  let date

  switch (duration) {
    case Timescale.Day:
      date = moment(startDate)
      break
    case Timescale.Week:
      date = moment(startDate).endOf('week')
      break
    case Timescale.Month:
      date = moment(startDate).endOf('month')
      break
    case Timescale.Year:
      date = moment(startDate).endOf('year')
      break
    default:
      break
  }

  return date
}

const defaultStartDate = (props) => {
  const { duration, startDate } = props
  let date

  switch (duration) {
    case Timescale.Day:
      date = moment(startDate)
      break
    case Timescale.Week:
      date = moment(startDate).startOf('week')
      break
    case Timescale.Month:
      date = moment(startDate).startOf('month')
      break
    case Timescale.Year:
      date = moment(startDate).startOf('year')
      break
    default:
      break
  }

  return date
}

const initialValues = (props) => ({
  filters: '',
  filename: props.defaultFilename,
  searchTerms: props.searchTerms || '',
  filterSearchBarFocus: false,
  startDate: defaultStartDate(props),
  endDate: defaultEndDate(props),
  rowCount: props.rowCount,
  contentOptions: props.contentOptions,
})

class ExportToCSV extends Component {
  constructor(props) {
    super(props)

    this.state = {
      ...initialValues(props),
      filterProps: {},
      modalVisible: props.modalVisible,
    }

    this.submit = this.submit.bind(this)
    this.toggleModal = this.toggleModal.bind(this)
    this.updateCSVRowCount = this.updateCSVRowCount.bind(this)
    this.updateEndDate = this.updateEndDate.bind(this)
    this.updateFilters = this.updateFilters.bind(this)
    this.updateFilename = this.updateFilename.bind(this)
    this.updateSearchTerms = this.updateSearchTerms.bind(this)
    this.updateStartDate = this.updateStartDate.bind(this)
    this.updateAndCount = this.updateAndCount.bind(this)
    this.toggleIncludeContentOptions =
      this.toggleIncludeContentOptions.bind(this)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const visibilityPropChanged =
      this.props.modalVisible !== nextProps.modalVisible
    const modalVisible = visibilityPropChanged
      ? nextProps.modalVisible
      : this.state.modalVisible
    const modalOpened = modalVisible && visibilityPropChanged
    let callback = () => {}
    let newState

    if (modalOpened) {
      newState = {
        ...initialValues(nextProps),
        ...nextProps.initialSelectedValues,
        value: nextProps.value,
        modalVisible,
      }

      callback = () => {
        this.updateCSVRowCount()
      }
    } else {
      newState = { modalVisible }
    }

    this.setState(newState, callback)
  }

  toggleIncludeContentOptions() {
    this.setState((prevState) => ({
      contentOptions: !prevState.contentOptions,
    }))
  }

  get csvParams() {
    const propsAndState = { ...this.props, ...this.state }
    propsAndState.filterProps.value = propsAndState.value
    return this.props.csvParams(propsAndState)
  }

  updateFilterProps(filters) {
    const currentProps = this.state.filterProps
    const newProps = { ...currentProps, value: filters }
    this.setState(newProps)
  }

  submitDisabled() {
    const hasRows = this.props.rowCount > 0
    const validRowCount = this.props.rowCount <= MAX_ROW_COUNT

    return !(this.hasFilename() && hasRows && validRowCount)
  }

  get formMeta() {
    const rowText = this.props.rowCount === 1 ? 'row' : 'rows'
    const validRowCount = this.props.rowCount <= MAX_ROW_COUNT
    const info = validRowCount
      ? `${this.props.rowCount} ${rowText} in CSV`
      : TOO_MANY_ROWS_MESSAGE

    return {
      info,
      title: 'Total: ',
      warn: this.props.rowCount === 0,
      error: !validRowCount,
    }
  }

  hasFilename() {
    return this.state.filename.length > 0
  }

  submit() {
    this.props.createCSV(this.csvParams)
    this.toggleModal()

    if (this.props.onSubmit) {
      this.props.onSubmit()
    }
  }

  toggleModal() {
    let newState
    let backboneDelegate

    if (this.state.modalVisible) {
      backboneDelegate = this.props.onClose
      newState = {
        modalVisible: !this.state.modalVisible,
      }
    } else {
      backboneDelegate = this.props.onOpen
      newState = {
        ...initialValues(this.props),
        modalVisible: !this.state.modalVisible,
      }
    }

    if (backboneDelegate) {
      backboneDelegate()
    } else {
      this.setState(newState)
    }
  }

  updateFilename(value) {
    this.setState({ filename: value })
  }

  updateSearchTerms(value) {
    this.updateAndCount({ searchTerms: value })
  }

  updateStartDate({ due_date }) {
    this.updateAndCount({ startDate: due_date.toDate() })
  }

  updateEndDate({ due_date }) {
    this.updateAndCount({ endDate: due_date.toDate() })
  }

  updateFilters(selectedFilters, metaFilters) {
    let enabledFilters

    if (metaFilters) {
      enabledFilters = metaFilters.disabled
        ? []
        : selectedFilters.filter(
            (filter) =>
              metaFilters && !metaFilters.value.disabled.includes(filter.id)
          )
    } else {
      enabledFilters = selectedFilters
    }

    this.updateFilterProps(metaFilters.value.selected)

    const updatedFilters = this.props.filterSieve(enabledFilters)
    this.updateAndCount(updatedFilters)
  }

  updateAndCount(params) {
    this.setState(params, this.updateCSVRowCount)
  }

  updateCSVRowCount() {
    if (this.props.getRowCount) {
      this.props.getRowCount(this.csvParams)
    }
  }

  render() {
    return (
      <DefaultQueryClientProvider>
        <AnalyticsLayer name="CSV Export">
          <HotKey hotKey={HOTKEY} callback={this.toggleModal} />
          <Modal onClose={this.toggleModal} visible={this.state.modalVisible}>
            <div
              role="dialog"
              aria-labelledby="form-title"
              aria-modal
              className={kuiperClassNames('etc')}
            >
              <Form
                cancelText="Cancel"
                disableSubmit={this.submitDisabled()}
                formMeta={this.formMeta}
                onCancel={this.toggleModal}
                onSubmit={this.submit}
                submitText="Export"
                title={this.props.title}
              >
                <TextInput
                  className={kuiperClassNames('etc-name')}
                  focusOnMount={true}
                  onChange={this.updateFilename}
                  placeholder={this.props.filenamePlaceholder}
                  theme="form"
                  title="Name"
                  valid={this.hasFilename()}
                  value={this.state.filename}
                />

                <ContentFilter
                  additionalFilterOptions={this.props.additionalFilterOptions}
                  disabledFilterCategories={this.props.disabledFilterCategories}
                  currentStoryUuid={this.props.currentStoryUuid}
                  contentOptions={this.state.contentOptions}
                  endDate={this.state.endDate}
                  onChange={this.updateFilters}
                  placeholder={this.props.searchPlaceholder}
                  searchTerms={this.state.searchTerms}
                  startDate={this.state.startDate}
                  toggleIncludeContentOptions={this.toggleIncludeContentOptions}
                  updateEndDate={this.updateEndDate}
                  updateSearchTerms={this.updateSearchTerms}
                  updateStartDate={this.updateStartDate}
                  withContentOptions={this.props.withContentOptions}
                  withDateRange={this.props.withDateRange}
                  withSearch={this.props.withSearch}
                  workspaceId={this.props.workspaceId}
                />
              </Form>
            </div>
          </Modal>
        </AnalyticsLayer>
      </DefaultQueryClientProvider>
    )
  }
}

ExportToCSV.propTypes = {
  additionalFilterOptions: arrayOf(object),
  createCSV: func,
  csvParams: func.isRequired,
  currentStoryUuid: string,
  disabledFilterCategories: arrayOf(string),
  filenamePlaceholder: string,
  filterSieve: func.isRequired,
  getRowCount: func.isRequired,
  initialSelectedValues: object,
  modalVisible: bool,
  onClose: func,
  onOpen: func,
  onSubmit: func,
  rowCount: number,
  searchPlaceholder: string,
  title: string,
  value: arrayOf(string),
  withContentOptions: bool,
  withDateRange: bool,
  withSearch: bool,
  workspaceId: number,
}

export default ExportToCSV
